<script setup lang="ts">
import { titleBar } from '@/composables/menu/title'
import { useI18n } from 'vue-i18n'
import { onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getOpenObituaryPeriod, type OpenObituaryResponse, type ObituaryDTO } from '@/composables/obituary/obituary'
import { CODE_OK } from '@/apis/code'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const requestURL = useRequestURL()

onMounted(() => {
  titleBar.value.hideBack = false
  titleBar.value.backable = true
  titleBar.value.transparent = false
  titleBar.value.title = t('m.open_memorial')
})

const nuxtApp = useNuxtApp() as any
if (!nuxtApp.payload.data) {
  nuxtApp.payload.data = {}
}
if (nuxtApp.static && !nuxtApp.static.data) {
  nuxtApp.static.data = {}
}

const currentPage = computed({
  get: () => Number(route.query.page) || 1,
  set: (val) => {
    router.replace({ query: { ...route.query, page: String(val) } })
  }
})

const canonicalUrl = computed(() => {
  const url = new URL(`${requestURL.origin}${route.path}`)
  if (route.query.page != null) {
    url.searchParams.set('page', String(route.query.page))
  }
  return url.toString()
})

// ---- 工具函数 ----

/** 从 periodStart/periodEnd 时间戳生成降序（最新在前）的年月标签数组 */
function generateTemporalList(periodStart: number, periodEnd: number): string[] {
  const start = new Date(periodStart)
  const end = new Date(periodEnd)
  const list: string[] = []
  let year = end.getFullYear()
  let month = end.getMonth()
  while (year > start.getFullYear() || (year === start.getFullYear() && month >= start.getMonth())) {
    list.push(`${year}年${month + 1}月`)
    month--
    if (month < 0) {
      month = 11
      year--
    }
  }
  return list
}

/** 将 "2025年1月" 解析为该月起止日期 */
function getMonthRange(label: string): { startDate: string; endDate: string } {
  const match = label.match(/(\d+)年(\d+)月/)
  if (!match) return { startDate: '', endDate: '' }
  const y = parseInt(match[1]),
    m = parseInt(match[2])
  const lastDay = new Date(y, m, 0).getDate()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return { startDate: `${y}-${pad(m)}-01`, endDate: `${y}-${pad(m)}-${pad(lastDay)}` }
}

/** 获取当前真实月份的起止日期 */
function getCurrentMonthRange() {
  const now = new Date()
  const y = now.getFullYear(),
    m = now.getMonth() + 1
  const lastDay = new Date(y, m, 0).getDate()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return { startDate: `${y}-${pad(m)}-01`, endDate: `${y}-${pad(m)}-${pad(lastDay)}` }
}

/** 在 temporalList 中查找当前月份对应的页码（1-based） */
function findCurrentMonthPage(list: string[]): number {
  const now = new Date()
  const label = `${now.getFullYear()}年${now.getMonth() + 1}月`
  const idx = list.indexOf(label)
  return idx >= 0 ? idx + 1 : 1
}

// ---- Phase 1: 用当前月份请求，获取 period 范围，构建时间列表（仅执行一次） ----

const temporalListState = useState<string[]>('obituary-temporal-list', () => [])

const { data: periodSsrData } = await useAsyncData('obituary-period', async () => {
  const { startDate, endDate } = getCurrentMonthRange()
  const { data } = await getOpenObituaryPeriod(startDate, endDate)
  return data.value
})

watch(
  () => periodSsrData.value,
  (value) => {
    if (value && value.code === CODE_OK) {
      const resp = value.data as OpenObituaryResponse
      temporalListState.value = generateTemporalList(resp.periodStart, resp.periodEnd)
    }
  },
  { immediate: true }
)

const temporalList = computed(() => temporalListState.value)
const totalPages = computed(() => temporalList.value.length)
const currentMonth = computed(() => temporalList.value[currentPage.value - 1] || '')

const pageTitle = computed(() => `${t('m.open_memorial')} ${currentMonth.value}`.trim())
const pageDescription = computed(() => `按月份聚合的公开讣告列表：${currentMonth.value}`.trim())

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

// ---- Phase 2: 根据 currentPage 获取对应月份的讣告数据（watch currentPage 自动刷新） ----

const obituaryPageState = useState<OpenObituaryResponse | null>('obituary-page', () => null)

const { data: pageSsrData } = await useAsyncData(
  'obituary-page-data',
  async () => {
    const list = temporalListState.value
    if (list.length === 0) return null
    const label = list[currentPage.value - 1]
    if (!label) return null
    const { startDate, endDate } = getMonthRange(label)
    const { data } = await getOpenObituaryPeriod(startDate, endDate)
    return data.value
  },
  { watch: [currentPage] }
)

watch(
  () => pageSsrData.value,
  (value) => {
    if (value && value.code === CODE_OK) {
      obituaryPageState.value = value.data as OpenObituaryResponse
    }
  },
  { immediate: true }
)

const openObituaries = computed(() => obituaryPageState.value?.openObituaries ?? [])
const onPageChange = () => {
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<template>
  <BaseLayout>
    <div class="min-h-screen w-full bg-white pb-12 px-4 flex flex-col">
      <OpenListLayout :title="`所有公开讣告 ${currentMonth}`" subtitle="">
        <div v-if="openObituaries.length === 0" class="text-center text-gray-400 py-8">
          {{ t('message.no_data') }}
        </div>
        <OpenListItem
          v-for="item in openObituaries"
          :key="item.id"
          :to="`/obituary/${item.id}`"
          :label="`${item.fullName}的讣告`" />
      </OpenListLayout>

      <div class="mt-4 pb-4" v-if="temporalList.length > 0 && totalPages > 1">
        <ResponsivePagination
          v-model="currentPage"
          :total="totalPages"
          mode="temporal"
          :temporalList="temporalList"
          @change="onPageChange"
          seoMode="seo" />
      </div>
    </div>
  </BaseLayout>
</template>

<style scoped></style>
